export interface ListedCollege {
    name: string;
    city?: string;
    state?: string;
}

const DATA_SOURCE_NAME =
    /^(the\s+)?(college\s+scorecard|college\s+board|college\s+navigator|ncaa(\.org)?|naia(\.org)?|njcaa(\.org)?|fairtest|nces)\b/i;

const SCHOOL_NAME =
    /^(the\s+)?(university|college|institute)\s+of\b|\b(university|college|institute|polytechnic)(\s+(of|at)\b|\s+[A-Z]|[,-]|$)/i;
const ADVICE_LINE =
    /[—–]|:\s|\b(can help|discuss your|talk to|who can i|reach out|available 24|suicide|helpline)\b/i;

function hasUsStateLocation(location: string): boolean {
    const parts = location.split(",").map((part) => part.trim()).filter(Boolean);
    if (parts.length === 0) {
        return false;
    }
    return /^[A-Za-z]{2}$/.test(parts[parts.length - 1]);
}

function looksLikeSchoolName(name: string, location = ""): boolean {
    if (name.length < 4 || DATA_SOURCE_NAME.test(name) || ADVICE_LINE.test(name)) {
        return false;
    }
    const wordCount = name.split(/\s+/).filter(Boolean).length;
    if (wordCount > 10 && !hasUsStateLocation(location)) {
        return false;
    }
    if (SCHOOL_NAME.test(name)) {
        return true;
    }
    return hasUsStateLocation(location);
}

export function parseListedCollegeFromText(text: string): ListedCollege | null {
    if (!text) {
        return null;
    }
    const trimmed = text.trim().replace(/\s+/g, " ");
    const bold = /^\s*(?:[-*]|\d+\.)?\s*\*\*([^*]+)\*\*(?:\s*\(([^)]+)\))?/.exec(trimmed);
    const match = bold ?? /^(.+?)(?:\s*\(([^)]+)\))?\s*$/.exec(trimmed);
    if (!match) {
        return null;
    }
    const name = match[1].trim().replace(/\s+/g, " ").replace(/^\*\*|\*\*$/g, "");
    const location = (match[2] ?? "").trim();
    if (!looksLikeSchoolName(name, location)) {
        return null;
    }
    const locationParts = location.split(",").map((part) => part.trim()).filter(Boolean);
    const state =
        locationParts.length > 0 && /^[A-Za-z]{2}$/.test(locationParts[locationParts.length - 1])
            ? locationParts[locationParts.length - 1].toUpperCase()
            : undefined;
    const city =
        locationParts.length > 1
            ? locationParts.slice(0, -1).join(", ")
            : locationParts.length === 1 && !state
              ? locationParts[0]
              : locationParts.length === 1 && state
                ? undefined
                : locationParts[0];
    return { name, city, state };
}

export function collegeNamesMatch(left: string | undefined, right: string | undefined): boolean {
    const a = (left ?? "").trim().toLowerCase();
    const b = (right ?? "").trim().toLowerCase();
    if (!a || !b) {
        return false;
    }
    return a === b || a.includes(b) || b.includes(a);
}

export function extractListedColleges(markdown: string, limit = 100): ListedCollege[] {
    if (!markdown) {
        return [];
    }
    const seen = new Set<string>();
    const found: ListedCollege[] = [];
    for (const line of markdown.split(/\r?\n/)) {
        const college = parseListedCollegeFromText(line);
        if (!college) {
            continue;
        }
        const key = college.name.toLowerCase();
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        found.push(college);
        if (found.length >= limit) {
            break;
        }
    }
    return found;
}
